import HideCrisp from '@components/atoms/HideCrisp';
import {
  ConductorInput,
  HouseType,
  MaritalStatus,
  QuoteType,
  Sex,
  useCreateQuoteMutation,
  usePrefillQuoteWithViewerDataQuery,
  UserIntent,
  VehicleType,
  VehicleUtilization,
  YoungConductorCoverage,
} from '@graphql/generated';
import COLORS from '@lib/constants/colors';
import { QUOTE_QUESTIONS } from '@lib/data/questions';
import { QuoteInitialForm } from '@lib/data/typesQuestions';
import localStorageHelper from '@lib/helpers/localStorage';
import {
  getNextQuestionId,
  initialFormQuestions,
} from '@lib/helpers/questions';
import { useUser } from '@lib/hooks/auth';
import { useLocalStorage } from '@lib/hooks/localStorage';
import PageContainer from 'components/molecules/PageContainer';
import { onlyNumbers } from 'helpers';
import ROUTES from 'lib/constants/routes';
import { getActualQuestionV2, percentage } from 'lib/helpers/misc';
import { trackEvent } from 'lib/services/analytics';
import omit from 'lodash.omit';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

const LauraSmartForm = dynamic(
  () => import('components/organisms/LauraSmartForm'),
  { ssr: false },
);

const Global = createGlobalStyle`
body  {
  background-color: ${COLORS.pastel};
}
`;

const Container = styled(PageContainer)`
  background-color: ${COLORS.pastel};

  nav:first-child {
    background-color: ${COLORS.pastel};
    border-color: transparent;
  }
`;

const LOCAL_STORAGE_KEY = 'quote-initial-form';

const QuotePage: NextPage = () => {
  const { user } = useUser();
  const router = useRouter();
  const { q: questionId = 'start', vehicleId, suhaiLead } = router.query;
  const [quote, setQuote] = useLocalStorage<QuoteInitialForm>(
    LOCAL_STORAGE_KEY,
    {},
  );
  const [prefillState] = usePrefillQuoteWithViewerDataQuery({
    pause: !user || !vehicleId,
    variables: {
      vehicleId: `${vehicleId}`,
    },
  });

  useEffect(() => {
    if (suhaiLead && !quote.userIntent) {
      setQuote({ ...quote, userIntent: UserIntent.SuhaiLead });
    }
  }, [suhaiLead, setQuote, quote]);

  useEffect(() => {
    const vehicle = prefillState.data?.viewer.vehicle;
    if (vehicle) {
      setQuote({
        ...quote,
        vehicleVersions: [
          {
            brand: vehicle.brand,
            model: vehicle.model,
            modelYear: vehicle.modelYear,
            manufacturingYear: vehicle.manufacturingYear,
            fipe: vehicle.fipe,
            plate: vehicle.plate,
            chassi: vehicle.id,
            type: vehicle.type,
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillState.data?.viewer.vehicle]);

  const [, createQuoteMutation] = useCreateQuoteMutation();

  const getQuestion = (quoteData: QuoteInitialForm) =>
    initialFormQuestions(quoteData, prefillState.data);

  const questions = getQuestion(quote);

  const isFirstQuestion = questionId === questions[0].id;

  const question =
    getActualQuestionV2({
      questions,
      item: {
        ...quote,
        vehicle: { ...quote.vehicle, plate: '-' },
        mainConductor: { name: '-' },
      },
      currentId: questionId as string,
      requiredFields: [
        'type',
        'userIntent',
        'oldPolicy.carrier',
        'oldPolicy.bonus',
        'vehicle.plate',
        'vehicle.type',
        'vehicle.model',
        'vehicle.brand',
        'vehicle.zeroKm',
        'vehicle.manufacturingYear',
        'vehicle.overnightAddress.cep',
        'vehicle.utilization',
        'vehicle.houseType',
        'vehicle.houseParking',
        'vehicle.workParking',
        'vehicle.schoolParking',
        'conductor.cpf',
        'conductor.name',
        'conductor.sex',
        'conductor.birthday',
        'conductor.maritalStatus',
        'mainConductor.name',
        'youngCoverage',
        'email',
        'phoneNumber',
      ],
    }) || null;

  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  function changeQuestion(newId: string) {
    router.push(ROUTES.startForm(newId), undefined, {
      shallow: true,
    });
  }
  const memoizedChangeQuestion = useCallback(changeQuestion, [router]);

  useEffect(() => {
    if (!question && quote) {
      memoizedChangeQuestion(QUOTE_QUESTIONS[0].id);
    }
  }, [question, quote, memoizedChangeQuestion]);

  const handleQuestionSubmit = async (input: QuoteInitialForm) => {
    if (loading) return;
    setLoading(true);
    trackEvent('change onboarding step', {
      ...input,
      questionId: question?.id,
    });

    const prefilled =
      input.vehicle?.type === VehicleType.Trucks
        ? {
            ...input,
            vehicle: {
              ...input.vehicle,
              houseType: HouseType.House,
              utilization: [VehicleUtilization.Freight],
            },
          }
        : input;

    const newData = { ...quote, ...prefilled };

    setQuote(newData);

    if (
      input.phoneNumber ||
      (input.youngCoverage && prefillState.data?.viewer.email)
    ) {
      const referral = localStorageHelper.getReferral();

      setCompleted(true);

      trackEvent('finished onboarding form');

      if (!newData.vehicle?.plate && newData.vehicle) {
        newData.vehicle.plate = (newData as any).plate || null;
      }

      const { data } = await createQuoteMutation({
        input: {
          referral,
          conductor: {
            cpf: newData.conductor?.cpf as string,
            maritalStatus: newData.conductor?.maritalStatus as MaritalStatus,
            birthday: newData.conductor?.birthday as number,
            name: newData.conductor?.name as string,
            sex: newData.conductor?.sex as Sex,
          },
          phoneNumber:
            prefillState.data?.viewer.phoneNumber ||
            onlyNumbers(newData.phoneNumber as string),
          email: prefillState.data?.viewer.email || (newData.email as string),
          vehicle: omit(newData.vehicle, '__typename'),
          mainConductor: newData.mainConductor as ConductorInput | null,
          youngCoverage: newData.youngCoverage as YoungConductorCoverage,
          userIntent: newData.userIntent as UserIntent,
          type: newData.type as QuoteType,
          oldPolicy: newData.oldPolicy,
        },
      });

      if (data) {
        router.push(ROUTES.leadBroker(data?.createQuote.id));

        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } else {
      const newId = getNextQuestionId({
        questions: getQuestion(newData),
        actualId: questionId as string,
      });

      if (newId) changeQuestion(newId);
    }

    setLoading(false);
  };

  return (
    <>
      <Global />
      <HideCrisp />
      <Container
        title="Cotação de Seguro de Carro e Moto Online | Luna"
        metaDescription="Cote o seguro auto em menos de 1 minuto, veja os preços do seguro de carro e moto totalmente online, compare os valores das principais seguradoras do mercado."
        showNavBar={false}
        showFooter={false}
        loading={!router.isReady}
      >
        <LauraSmartForm
          question={question}
          item={quote}
          loading={loading}
          handleQuestionSubmit={handleQuestionSubmit}
          skipWelcome={!isFirstQuestion}
          // errorMessage={upsertQuoteState.error?.message.replace('[GraphQL] ', '')}
          progress={
            completed
              ? 100
              : percentage(
                  questions.findIndex((q) => q.id === questionId),
                  questions.length,
                )
          }
          onBackClick={() =>
            isFirstQuestion ? router.push(ROUTES.root) : router.back()
          }
          // rightNavBar={
          //   <PrimaryButton onClick={showModal}>
          //     <PhoneCallIcon />
          //   </PrimaryButton>
          // }
        />
      </Container>
    </>
  );
};

export default QuotePage;
